import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Heart, Loader2, Sun, Moon, Star, Sparkles, 
  UserPlus, History, CheckCircle, AlertTriangle, Crown, Lock
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const Synastry = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [premiumRequired, setPremiumRequired] = useState(null);

  // Form state
  const [partnerName, setPartnerName] = useState("");
  const [partnerBirthDate, setPartnerBirthDate] = useState("");
  const [partnerBirthTime, setPartnerBirthTime] = useState("12:00");
  const [partnerCity, setPartnerCity] = useState("");
  const [partnerState, setPartnerState] = useState("");
  const [partnerCountry, setPartnerCountry] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchHistory();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/astrology/synastry/history`, { headers, withCredentials: true });
      setHistory(res.data.reports || []);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCalculate = async () => {
    if (!partnerName || !partnerBirthDate || !partnerCity || !partnerCountry) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/astrology/synastry`, {
        partner_name: partnerName,
        partner_birth_date: partnerBirthDate,
        partner_birth_time: partnerBirthTime,
        partner_city: partnerCity,
        partner_state: partnerState,
        partner_country: partnerCountry
      }, { headers, withCredentials: true });

      // Check if premium required
      if (res.data.premium_required) {
        setPremiumRequired(res.data);
        setResult(null);
        return;
      }

      setResult(res.data);
      setPremiumRequired(null);
      toast.success("Compatibility calculated! ✨");
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate compatibility");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 65) return "text-emerald-400";
    if (score >= 50) return "text-amber-400";
    return "text-orange-400";
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return "from-green-500/20 to-emerald-500/20";
    if (score >= 65) return "from-emerald-500/20 to-teal-500/20";
    if (score >= 50) return "from-amber-500/20 to-yellow-500/20";
    return "from-orange-500/20 to-red-500/20";
  };

  return (
    <div className="space-y-6" data-testid="synastry-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Heart className="w-8 h-8 text-pink-400" />
          Partner Compatibility
        </h1>
        <p className="text-mystic-200/70">
          Discover cosmic connections through birth chart compatibility analysis
        </p>
      </motion.div>

      <Tabs defaultValue="calculate" className="w-full">
        <TabsList className="grid grid-cols-2 bg-mystic-800/50 p-1 rounded-xl mb-6">
          <TabsTrigger 
            value="calculate" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            New Reading
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg"
          >
            <History className="w-4 h-4 mr-2" />
            Past Readings
          </TabsTrigger>
        </TabsList>

        {/* Calculate Tab */}
        <TabsContent value="calculate" className="space-y-6">
          {/* Input Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-pink-400" />
                  Enter Partner's Birth Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Partner's Name *</Label>
                    <Input
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      placeholder="Enter name"
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="partner-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Birth Date *</Label>
                    <Input
                      type="date"
                      value={partnerBirthDate}
                      onChange={(e) => setPartnerBirthDate(e.target.value)}
                      className="bg-mystic-900/50 border-white/10 text-white"
                      data-testid="partner-birthdate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Birth Time (optional)</Label>
                    <Input
                      type="time"
                      value={partnerBirthTime}
                      onChange={(e) => setPartnerBirthTime(e.target.value)}
                      className="bg-mystic-900/50 border-white/10 text-white"
                      data-testid="partner-birthtime-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">City *</Label>
                    <Input
                      value={partnerCity}
                      onChange={(e) => setPartnerCity(e.target.value)}
                      placeholder="e.g., Los Angeles"
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="partner-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">State/Province</Label>
                    <Input
                      value={partnerState}
                      onChange={(e) => setPartnerState(e.target.value)}
                      placeholder="e.g., California"
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="partner-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-100">Country *</Label>
                    <Input
                      value={partnerCountry}
                      onChange={(e) => setPartnerCountry(e.target.value)}
                      placeholder="e.g., USA"
                      className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="partner-country-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 mt-4"
                  data-testid="calculate-synastry-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Heart className="w-5 h-5 mr-2" />
                      Calculate Compatibility
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Premium Required */}
          {premiumRequired && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="glass border-moon-gold/30 bg-gradient-to-br from-moon-gold/10 to-pink-500/5">
                <CardContent className="py-12 text-center">
                  <Lock className="w-16 h-16 text-moon-gold mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
                  <p className="text-mystic-200/70 mb-4 max-w-md mx-auto">
                    {premiumRequired.message}
                  </p>
                  
                  {/* Show feature preview */}
                  {premiumRequired.preview?.features && (
                    <div className="glass-light rounded-xl p-4 mb-6 max-w-md mx-auto text-left">
                      <p className="text-mystic-300 text-sm mb-3 text-center">What you'll unlock:</p>
                      <ul className="space-y-2">
                        {premiumRequired.preview.features.map((feature, idx) => (
                          <li key={`k-${idx}`} className="text-mystic-200/70 text-sm flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-pink-400 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Link to="/settings">
                    <Button className="bg-moon-gold hover:bg-moon-gold/90 text-mystic-900">
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade to Premium
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Results */}
          {result && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Score Card */}
              <Card className={`glass bg-gradient-to-br ${getScoreGradient(result.compatibility_score)}`}>
                <CardContent className="py-8 text-center">
                  <p className="text-mystic-300 text-sm mb-2">Compatibility with {result.partner_name}</p>
                  <p className={`text-6xl font-bold mb-2 ${getScoreColor(result.compatibility_score)}`}>
                    {result.compatibility_score}%
                  </p>
                  <p className="text-xl text-white font-medium mb-2">{result.overall_rating}</p>
                  <p className="text-mystic-200/70 max-w-lg mx-auto">{result.interpretation}</p>
                </CardContent>
              </Card>

              {/* Signs Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-mystic-300">Your Signs</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Sun className="w-4 h-4 text-amber-400" /> Sun
                      </span>
                      <Badge className="bg-amber-500/20 text-amber-300">{result.user_signs?.sun}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Moon className="w-4 h-4 text-indigo-400" /> Moon
                      </span>
                      <Badge className="bg-indigo-500/20 text-indigo-300">{result.user_signs?.moon}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-400" /> Venus
                      </span>
                      <Badge className="bg-pink-500/20 text-pink-300">{result.user_signs?.venus}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Star className="w-4 h-4 text-red-400" /> Mars
                      </span>
                      <Badge className="bg-red-500/20 text-red-300">{result.user_signs?.mars}</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-mystic-300">{result.partner_name}'s Signs</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Sun className="w-4 h-4 text-amber-400" /> Sun
                      </span>
                      <Badge className="bg-amber-500/20 text-amber-300">{result.partner_signs?.sun}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Moon className="w-4 h-4 text-indigo-400" /> Moon
                      </span>
                      <Badge className="bg-indigo-500/20 text-indigo-300">{result.partner_signs?.moon}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-400" /> Venus
                      </span>
                      <Badge className="bg-pink-500/20 text-pink-300">{result.partner_signs?.venus}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-mystic-200/70 flex items-center gap-2">
                        <Star className="w-4 h-4 text-red-400" /> Mars
                      </span>
                      <Badge className="bg-red-500/20 text-red-300">{result.partner_signs?.mars}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Aspects */}
              {result.aspects && result.aspects.length > 0 && (
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Compatibility Aspects</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {result.aspects.map((aspect, idx) => (
                      <div 
                        key={`k-${idx}`}
                        className={`glass-light rounded-lg p-4 border-l-4 ${
                          aspect.harmony === 'positive' ? 'border-green-500' :
                          aspect.harmony === 'challenging' ? 'border-amber-500' :
                          'border-mystic-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-medium">{aspect.type}</span>
                          {aspect.harmony === 'positive' ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : aspect.harmony === 'challenging' ? (
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                          ) : null}
                        </div>
                        <p className="text-mystic-200/70 text-sm">{aspect.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Advice */}
              {result.advice && result.advice.length > 0 && (
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-moon-gold" />
                      Cosmic Advice
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {result.advice.map((advice, idx) => (
                        <li key={`k-${idx}`} className="flex items-start gap-2 text-mystic-200/80">
                          <Star className="w-4 h-4 text-moon-gold mt-0.5 flex-shrink-0" />
                          <span>{advice}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
            </div>
          ) : history.length === 0 ? (
            <Card className="glass">
              <CardContent className="py-12 text-center">
                <History className="w-12 h-12 text-mystic-500 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No Past Readings</h3>
                <p className="text-mystic-200/70">
                  Calculate your first compatibility reading to see it here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {history.map((report, idx) => (
                <motion.div
                  key={report.synastry_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card className="glass">
                    <CardContent className="py-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{report.partner_name}</p>
                        <p className="text-mystic-400 text-sm">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getScoreColor(report.score)}`}>
                          {report.score}%
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Synastry;
